import { SERVER } from '../../../config/config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Forms = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, [currentPage]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER}/form-submissions`, {
        params: { page: currentPage, limit: 10 },
        withCredentials: true
      });
      setSubmissions(response.data.submissions);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      alert('Error fetching submissions');
    } finally {
      setLoading(false);
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await axios.delete(`${SERVER}/form-submissions/${id}`, {
        withCredentials: true
      });
      alert('Submission deleted successfully');
      fetchSubmissions();
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Error deleting submission');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-t-blue-600 absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header Card */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-3xl shadow-2xl p-8 animate-scaleIn">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/20 rounded-full -ml-48 -mb-48 blur-3xl" />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Form Submissions</h2>
            <p className="text-blue-100">Manage and review all form submissions</p>
          </div>
          <div className="px-6 py-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
            <span className="text-white font-bold text-2xl">{submissions.length}</span>
            <span className="text-blue-100 text-sm ml-2">submissions</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {submissions.map((submission, index) => (
          <div key={submission.id} className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-slate-200/50 animate-slideUp" style={{ animationDelay: `${index * 0.1}s` }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-slate-200/50">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-lg">{submission.fullname.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">{submission.fullname}</h3>
                    <span className="text-xs text-slate-500">{formatDate(submission.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <span className="text-sm font-semibold text-slate-600 min-w-[100px]">📧 Email:</span>
                <span className="text-sm text-slate-900 flex-1 break-all">{submission.email}</span>
              </div>
              
              {submission.phone && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm font-semibold text-slate-600 min-w-[100px]">📱 Phone:</span>
                  <span className="text-sm text-slate-900 flex-1">{submission.phone}</span>
                </div>
              )}
              
              {submission.company_name && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm font-semibold text-slate-600 min-w-[100px]">🏢 Company:</span>
                  <span className="text-sm text-slate-900 flex-1">{submission.company_name}</span>
                </div>
              )}

              {submission.region && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm font-semibold text-slate-600 min-w-[100px]">📍 Region:</span>
                  <span className="text-sm text-slate-900 flex-1">{submission.region}</span>
                </div>
              )}
              
              {submission.message && (
                <div className="flex flex-col gap-2 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm font-semibold text-slate-600">💬 Message:</span>
                  <p className="text-sm text-slate-900 leading-relaxed">{submission.message}</p>
                </div>
              )}
              
              {submission.uploaded_image && (
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-semibold text-slate-600">🖼️ Uploaded Image:</span>
                  <div className="relative overflow-hidden rounded-xl">
                    <img 
                      src={`${SERVER}/${submission.uploaded_image}`} 
                      alt="Uploaded" 
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <span className="text-sm text-slate-500 hidden p-4 bg-slate-100 rounded-xl">Image not available</span>
                  </div>
                </div>
              )}
              
              {submission.social_links && submission.social_links.length > 0 && (
                <div className="flex flex-col gap-2 p-3 bg-slate-50 rounded-xl">
                  <span className="text-sm font-semibold text-slate-600">🔗 Social Links:</span>
                  <div className="space-y-2">
                    {submission.social_links.map((link, idx) => (
                      <a 
                        key={idx} 
                        href={link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="block text-sm text-blue-600 hover:text-blue-800 hover:underline truncate transition-colors"
                      >
                        {link}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <span className="text-sm font-semibold text-slate-600">💳 Transaction:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${submission.wants_transaction ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                  {submission.wants_transaction ? '✓ Yes' : '✕ No'}
                </span>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-6 border-t border-slate-200/50 bg-slate-50/50">
              <button 
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] flex items-center justify-center gap-2 group"
                onClick={() => deleteSubmission(submission.id)}
              >
                <span>🗑️</span>
                <span>Delete Submission</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {submissions.length === 0 && (
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-16 text-center animate-scaleIn">
          <div className="w-24 h-24 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">📋</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-800 mb-2">No Submissions Yet</h3>
          <p className="text-slate-500">Form submissions will appear here once users submit the form.</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-slate-200/50">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
          >
            ← Previous
          </button>
          
          <span className="text-sm font-semibold text-slate-700 px-4 py-2 bg-slate-100 rounded-xl">
            Page {currentPage} of {totalPages}
          </span>
          
          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default Forms;